/* eslint-disable no-param-reassign */
import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import {
  DocumentStates,
  DocumentTextProcessing,
  PDFTool,
  InputAnnotation,
  InspectionStatuses,
  DifferenceViewOptions,
  AllDocumentTypes,
} from 'types';
import {
  DocumentType,
  SearchedAnnotation,
  PageRangeOption,
  DocumentTypes,
  PageWidthFit,
  InspectionSettings,
  PDFTRON_DEFAULT_TOOL,
  OPPOSITE_DOCUMENT,
  InputDocuments,
  AutoMatchGraphic,
  Layer,
  CTFModuleTypes,
  Separation,
  GraphicsOptionValue,
  BarcodeSettings,
  PDFTronTools,
  PersistedDifferenceComment,
} from 'types/inspection';
import { DocumentToast } from 'components/DocumentToast/DocumentToast';
import { initialDocumentState, initialInspectionState } from './inspectionState';
import type { DifferenceViewOptionsPayload } from 'types/inspection';
import { CtfImagesResult, CtfMatchResult } from 'store/request/match/types';
import { JobMetaData } from 'types/inspection';
import { ColorId, GraphicsOptionKey, GraphicsSensitivity, MatchingMode } from '@verify/shared-utils';

type DocumentState = {
  documentType: DocumentType | 'report';
  documentState: DocumentStates;
};
type DocumentTextState = {
  documentType: DocumentType;
  documentTextProcessing: DocumentTextProcessing;
};

type SelectedTool = {
  tool: PDFTool;
};

type DocumentTypeRanges = { documentType: DocumentType; pageRange: Array<number> };

// TODO this should be renamed to InputAnnotationsPayload, as it doesnt represent 'all' annotations anymore, just the ones related to input tools (text, marquee, etc)
type AllAnnotationsPayload = {
  documentType: DocumentType;
  annotations: InputAnnotation[];
};

type LiveTextPayload = {
  documentType: DocumentType;
  liveText: boolean;
};

type SetZoneSelectedPayload = {
  zoneId: number;
};

type SetDrawZoneSelectedPayload = {
  zoneId: number;
};

type AddZoneSelectedTextPayload = {
  documentType: DocumentType;
  zoneId: string;
  annotationId: string;
  selectedText: string;
  selected?: boolean;
};

type AddManualSelectedZonePayload = {
  zoneId: number;
  annotationId: string;
  text: string;
};

type SetHasResultPayload = {
  zoneId: string;
  hasResult: boolean;
};

type ModifySelectedZonePayload = {
  zoneId: string;
  selectedText: string;
};

type ModifySearchedZonePayload = {
  annotationId: string;
  searchedText: string;
};

type ActivateManualSelectionPayload = {
  annotationId: string;
  zoneId: number;
};

type AddZoneSearchedAnnotationPayload = {
  searchedAnnotation: SearchedAnnotation;
};

type DeleteZoneSelectedZonePayload = {
  documentType: DocumentType;
  annotationId: string;
};

type DeleteSelectedZonesPayload = {
  documentType: DocumentType;
  annotationIds: string[];
};

type DeleteUnselectedTextPayload = {
  annotationId: string;
  zoneId: number;
};

type DeleteZoneSelectedTextPayload = {
  documentType: DocumentType;
  annotationId: string;
};

type PagesStatesMergePayload = {
  documentType: DocumentType;
  thumbnail: string;
  pageNumber: number;
};

type CurrentPage = {
  documentType: DocumentType;
  currentPage: number;
};

type TotalPageCount = {
  documentType: DocumentType | 'report';
  totalPageCount: number;
};

type DocumentTypePayload = {
  documentType: DocumentType;
};

type SetInspectionDataPayload = {
  id?: string;
  name?: string;
  inspectionStatus?: InspectionStatuses;
  lastJobId?: string | null;
  lastJobInput?: InputDocuments | null;
  lastJobCtfVersion?: string | null;
  lastJobCreatedAt?: string | null;
  settings?: InspectionSettings;
  automMatchGraphic?: AutoMatchGraphic;
  updatedAt?: string;
  customDictionaryIDs?: string[];
  isFilesLoaded?: boolean;
  ctfJobs?: CTFModuleTypes[];
  textMatchJobId?: string | null;
  textMatchCdnPathPrefix?: string | null;
  textMatchMetadata?: {
    masterPages: Record<string, number>;
    samplePages: Record<string, number>;
    totalMatches: number;
  } | null;
  lastJobMatches?: CtfMatchResult[];
  lastJobImages?: CtfImagesResult;
  lastJobUnmatchedCropIds?: string[]; // unmatched crop IDs from graphics job
  lastJobMetadata?: JobMetaData;
  userId?: string;
};

type DocumentMarkupsPayload = {
  documentType: DocumentType;
  annotations: InputAnnotation[];
  pageRange: Array<number>;
};

type ZoomLockLevelPayload = {
  zoom: null | number | PageWidthFit;
  documentType: AllDocumentTypes;
};

type showLibraryPayload = {
  type: DocumentTypes;
  value: boolean;
};

type zoomOptionPayload = {
  type: AllDocumentTypes;
  value: '' | PageWidthFit;
};

type setDocumentToastPayload = {
  documentType: DocumentTypes;
  toast: DocumentToast;
};

type setPendingShapeRequestPayload = {
  fileId: string;
  documentType: DocumentTypes;
  layerOrSeparationName: string;
  type: 'Layer' | 'Separation';
} | null;

const selectZone = createAction('inspection/selectZone', (annotationId: string) => ({
  payload: annotationId,
}));

const excludePage = createAction('inspection/excludePage', (pageNumber: number, documentType: DocumentTypes) => ({
  payload: {
    pageNumber,
    documentType,
  },
}));

const excludePages = createAction('inspection/excludePages', (pageNumbers: number[], documentType: DocumentTypes) => ({
  payload: {
    pageNumbers,
    documentType,
  },
}));

const inspection = createSlice({
  name: 'inspection',
  initialState: initialInspectionState,
  reducers: {
    setLoadingState: (state, action: PayloadAction<DocumentState>) => {
      state[action.payload.documentType].state = action.payload.documentState;
    },
    setSelectedTool: (state, action: PayloadAction<SelectedTool>) => {
      state.selectedTool = action.payload.tool;
    },
    setPageRange: (state, action: PayloadAction<DocumentTypeRanges>) => {
      state[action.payload.documentType].pageRange = action.payload.pageRange;
    },
    setAnnotations: (state, action: PayloadAction<AllAnnotationsPayload>) => {
      state[action.payload.documentType].annotations = action.payload.annotations;
    },
    setLiveTextProcess: (state, action: PayloadAction<DocumentTextState>) => {
      state[action.payload.documentType].documentTextProcessing = action.payload.documentTextProcessing;
    },
    setLiveText: (state, action: PayloadAction<LiveTextPayload>) => {
      state[action.payload.documentType].liveText = action.payload.liveText;
    },
    addZoneSelectedTexts: (state, action: PayloadAction<AddZoneSelectedTextPayload>) => {
      state[action.payload.documentType].zoneSelectedTexts.push({
        annotationId: action.payload.annotationId,
        text: action.payload.selectedText,
        selected: action.payload.selected ?? false,
        hasResult: true,
        zoneId: action.payload.zoneId,
        searchedAnnotations: [],
      });
    },
    setHasResult: (state, action: PayloadAction<SetHasResultPayload>) => {
      const selectedZone = state.source.zoneSelectedTexts.find((zone) => zone.zoneId === action.payload.zoneId);
      if (selectedZone) {
        selectedZone.hasResult = action.payload.hasResult;
      }
    },
    setNoResultAnnotationId: (state, action: PayloadAction<string>) => {
      state.noResultAnnotationId = action.payload;
    },
    addManualSelectedZone: (state, action: PayloadAction<AddManualSelectedZonePayload>) => {
      const selectedZone = state.source.zoneSelectedTexts.find(
        (zone) => zone.zoneId === action.payload.zoneId.toString(),
      );
      if (selectedZone) {
        selectedZone.searchedAnnotations = [
          { annotationId: action.payload.annotationId, text: action.payload.text, zoneId: action.payload.zoneId },
        ];
        selectedZone.hasResult = true;
      }
    },
    modifySelectedZone: (state, action: PayloadAction<ModifySelectedZonePayload>) => {
      const selectedZone = state.source.zoneSelectedTexts.find((zone) => zone.zoneId === action.payload.zoneId);
      if (selectedZone) {
        selectedZone.text = action.payload.selectedText;
      }
    },
    modifySearchedZone: (state, action: PayloadAction<ModifySearchedZonePayload>) => {
      const selectedZone = state.source.zoneSelectedTexts.find(
        (zone) => zone.annotationId === action.payload.annotationId,
      );
      if (selectedZone) {
        selectedZone.searchedAnnotations[0].text = action.payload.searchedText;
      }
    },
    activateManualSelection: (state, action: PayloadAction<ActivateManualSelectionPayload>) => {
      state.manualSelectedZoneId = action.payload.zoneId;
      state.manualSelectedAnnotationId = action.payload.annotationId;
    },
    deactivateManualSelection: (state) => {
      state.manualSelectedZoneId = 0;
      state.manualSelectedAnnotationId = '';
    },
    setZoneSelected: (state, action: PayloadAction<SetZoneSelectedPayload>) => {
      const selectedTexts = state.source.zoneSelectedTexts.filter(
        (text) => Number(text.zoneId) === action.payload.zoneId,
      );
      if (selectedTexts) {
        selectedTexts.forEach((text) => {
          text.selected = true;
        });
      }
    },
    // quick fix to avoid autosave
    setDrawZoneSelected: (state, action: PayloadAction<SetDrawZoneSelectedPayload>) => {
      const selectedText = state.source.zoneSelectedTexts.find((text) => Number(text.zoneId) === action.payload.zoneId);
      if (selectedText) {
        selectedText.selected = true;
      }
    },
    setZoneUnselected: (state, action: PayloadAction<SetZoneSelectedPayload>) => {
      const selectedText = state.source.zoneSelectedTexts.find((text) => Number(text.zoneId) === action.payload.zoneId);
      if (selectedText) {
        selectedText.selected = false;
      }
    },
    addZoneSearchedAnnotation: (state, action: PayloadAction<AddZoneSearchedAnnotationPayload>) => {
      const selectedText = state.source.zoneSelectedTexts.find(
        (text) => text.annotationId === action.payload.searchedAnnotation.annotationId.split('_')[0],
      );
      selectedText?.searchedAnnotations.push(action.payload.searchedAnnotation);
    },
    deleteSelectedText: (state, action: PayloadAction<DeleteZoneSelectedTextPayload>) => {
      state[action.payload.documentType].zoneSelectedTexts = state[
        action.payload.documentType
      ].zoneSelectedTexts.filter((annotation) => annotation.annotationId !== action.payload.annotationId);
    },
    // update the zoneId for the rest of the list
    resetZoneId: (state) => {
      state.source.zoneSelectedTexts.forEach((zone, index) => {
        zone.zoneId = (index + 1).toString();
        zone.searchedAnnotations.forEach((searchedAnnot) => (searchedAnnot.zoneId = index + 1));
      });
    },
    deleteSelectedZone: (state, action: PayloadAction<DeleteZoneSelectedZonePayload>) => {
      state[action.payload.documentType].annotations = state[action.payload.documentType].annotations.filter(
        (annotation) => annotation.annotationId !== action.payload.annotationId,
      );
    },
    deleteSelectedZones: (state, action: PayloadAction<DeleteSelectedZonesPayload>) => {
      state[action.payload.documentType].annotations = state[action.payload.documentType].annotations.filter(
        (annotation) => !action.payload.annotationIds.includes(annotation.annotationId),
      );
    },
    deleteUnselectedText: (state, action: PayloadAction<DeleteUnselectedTextPayload>) => {
      const selectedText = state.source.zoneSelectedTexts.find((text) => Number(text.zoneId) === action.payload.zoneId);
      if (selectedText) {
        selectedText.searchedAnnotations = selectedText.searchedAnnotations.filter(
          (annot) => annot.annotationId === action.payload.annotationId,
        );
      }
    },
    resetSelectedTexts: (state) => {
      state[DocumentTypes.source].zoneSelectedTexts.length = 0;
    },
    setDifferencesLoaded: (state, action: PayloadAction<boolean>) => {
      state.differencesLoaded = action.payload;
    },
    setDifferenceViewOptions: (state, action: PayloadAction<DifferenceViewOptionsPayload>) => {
      if (action.payload.sortBy) {
        state.differenceViewOptions.sortBy = action.payload.sortBy;
      }

      if (action.payload.replaceSortByPerModule) {
        state.differenceViewOptions.sortByPerModule = action.payload.sortByPerModule;
      } else if (action.payload.sortByPerModule) {
        state.differenceViewOptions.sortByPerModule = {
          ...state.differenceViewOptions.sortByPerModule,
          ...action.payload.sortByPerModule,
        };
      }

      if (action.payload.differenceTypeFilters) {
        state.differenceViewOptions.differenceTypeFilters = action.payload.differenceTypeFilters;
      }

      if (action.payload.differenceStatusFilters) {
        state.differenceViewOptions.differenceStatusFilters = action.payload.differenceStatusFilters;
      }
    },
    setPersistedComment: (state, action: PayloadAction<PersistedDifferenceComment>) => {
      action.payload.diffIds.forEach((diffId) => {
        state.persistedDifferenceComments[diffId] = action.payload.comment;
      });
    },
    clearPersistedDifferenceComments: (state) => {
      state.persistedDifferenceComments = {};
    },
    focusDifference: (state, action: PayloadAction<string>) => {
      state.previousDifferenceId = state.focusedDifferenceId;
      state.focusedDifferenceId = action.payload;
    },
    unfocusDifference: (state) => {
      state.previousDifferenceId = state.focusedDifferenceId;
      state.focusedDifferenceId = '-1';
    },
    setLoadingDifferences: (state, action: PayloadAction<boolean>) => {
      state.reloadingDifferences = action.payload;
    },
    setResyncLoading: (state, action: PayloadAction<boolean>) => {
      state.resyncStatus.loading = action.payload;
      if (action.payload === false) {
        state.resyncStatus.processingResults = false;
      }
    },
    setProcessingResyncResults: (state, action: PayloadAction<boolean>) => {
      state.resyncStatus.processingResults = action.payload;
    },
    setUndoingResync: (state, action: PayloadAction<boolean>) => {
      state.resyncStatus.undoingResync = action.payload;
    },
    setResyncCancelled: (state, action: PayloadAction<boolean>) => {
      state.resyncStatus.cancelled = action.payload;
      if (action.payload === true) {
        state.resyncStatus.loading = false;
        state.resyncStatus.processingResults = false;
      }
    },
    setInvalidRatioScaledGraphicRatio: (state, action: PayloadAction<boolean>) => {
      state.invalidScaledGraphicZoneRatio = action.payload;
    },
    setUnconfirmTextZoneError: (state, action: PayloadAction<boolean>) => {
      state.unconfirmTextZoneError = action.payload;
    },
    setUnconfirmGraphicZoneError: (state, action: PayloadAction<boolean>) => {
      state.unconfirmedGraphicZoneError = action.payload;
    },
    setUnconfirmedMarqueeZoneError: (state, action: PayloadAction<boolean>) => {
      state.unconfirmedMarqueeZoneError = action.payload;
    },
    setChangeToolError: (state, action: PayloadAction<string>) => {
      state.changeToolError = action.payload;
    },
    setPendingCropZone: (
      state,
      action: PayloadAction<{ documentType: DocumentTypes | null; pendingCropId: string }>,
    ) => {
      state.pendingCropZone = action.payload;
    },
    setPendingShapeRequest: (state, action: PayloadAction<setPendingShapeRequestPayload>) => {
      state.pendingShapeRequest = action.payload;
    },
    mergePageState: (state, action: PayloadAction<PagesStatesMergePayload>) => {
      const { thumbnail, pageNumber, documentType } = action.payload;
      state[documentType].pages[pageNumber] = {
        thumbnail,
        loaded: true,
      };
    },
    setTotalPageCount: (state, action: PayloadAction<TotalPageCount>) => {
      state[action.payload.documentType].totalPageCount = action.payload.totalPageCount;
    },
    setCurrentPage: (state, action: PayloadAction<CurrentPage>) => {
      state[action.payload.documentType].currentPage = action.payload.currentPage;
    },
    toggleSyncScrolling: (state) => {
      state.syncScrolling = !state.syncScrolling;
    },
    resetDocumentState: (state, action: PayloadAction<DocumentTypePayload>) => {
      state[action.payload.documentType] = initialDocumentState;
      state[OPPOSITE_DOCUMENT[action.payload.documentType]].annotations = []; // clean up other document's annotations
      state.selectedTool = PDFTRON_DEFAULT_TOOL;
      state.inspectionStatus = InspectionStatuses.idle;
      state.lastJobId = null;
      state.tableEnhancementEnabledState = false;
      state.settings.tableEnhancement = false;
      state.settings.fullPageGraphics = false;
      state.settings.text = undefined;
      state.settings.textMatchingMode = undefined;
      if (action.payload.documentType === DocumentTypes.target) {
        state.settings.dictionaryName = undefined;
      }
      state.polygons = {
        ...state.polygons,
        [action.payload.documentType]: {
          points: '',
          type: '',
          key: '',
          layerOrSeparationName: '',
        },
      };
      state.nextTextZoneId = 1;
      state.nextGraphicZoneId = 1;
      state.nextCropZoneId = 1;
      state.nextMarqueeZoneId = 1;
      state.nextCrossoutZoneId = 1;
      state.nextColorPointId = 1;
      state.nextManualAlignZoneId = 1;
      state.manualAlignMode = initialInspectionState.manualAlignMode;
    },
    setInspectionId: (state, action: PayloadAction<string>) => {
      state.inspectionId = action.payload;
    },
    // this will reset the values we send to create a new inspection
    resetInspection: (state) => {
      state.name = '';
      state.inspectionId = '';
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setIsSharing: (state, action: PayloadAction<boolean>) => {
      state.settings.isSharing = action.payload;
    },
    setInspectionData: (state, action: PayloadAction<SetInspectionDataPayload>) => {
      const {
        id,
        name,
        inspectionStatus,
        lastJobId,
        lastJobInput,
        lastJobCtfVersion,
        lastJobCreatedAt,
        settings,
        updatedAt,
        customDictionaryIDs,
        isFilesLoaded,
        ctfJobs,
        textMatchJobId,
        textMatchCdnPathPrefix,
        textMatchMetadata,
        lastJobMatches,
        lastJobImages,
        lastJobUnmatchedCropIds,
        lastJobMetadata,
        userId,
      } = action.payload;
      state.inspectionId = id || state.inspectionId;
      state.name = name || state.name;
      state.inspectionStatus = inspectionStatus || state.inspectionStatus;
      state.lastJobId = lastJobId || state.lastJobId;
      state.lastJobInput = lastJobInput || state.lastJobInput;
      state.lastJobCtfVersion = lastJobCtfVersion !== undefined ? lastJobCtfVersion : state.lastJobCtfVersion;
      state.lastJobCreatedAt = lastJobCreatedAt !== undefined ? lastJobCreatedAt : state.lastJobCreatedAt;
      state.settings = settings || state.settings;
      state.updatedAt = updatedAt || state.updatedAt;
      state.customDictionaryIDs = customDictionaryIDs || state.customDictionaryIDs;
      state.isFilesLoaded = isFilesLoaded || false;
      state.ctfJobs = ctfJobs || state.ctfJobs;
      state.textMatchJobId = textMatchJobId !== undefined ? textMatchJobId : state.textMatchJobId;
      state.textMatchCdnPathPrefix =
        textMatchCdnPathPrefix !== undefined ? textMatchCdnPathPrefix : state.textMatchCdnPathPrefix;
      state.textMatchMetadata = textMatchMetadata !== undefined ? textMatchMetadata : state.textMatchMetadata;
      state.lastJobMatches = lastJobMatches || state.lastJobMatches;
      state.lastJobImages = lastJobImages || state.lastJobImages;
      state.lastJobUnmatchedCropIds = lastJobUnmatchedCropIds || state.lastJobUnmatchedCropIds;
      state.lastJobMetadata = lastJobMetadata || state.lastJobMetadata;
      state.userId = userId || state.userId;
    },
    resetLastJobInput: (state) => {
      state.lastJobInput = null;
    },
    resetStore: () => {
      return { ...initialInspectionState };
    },
    resetNameAndId: (state) => {
      state.inspectionId = '';
      state.name = '';
    },
    // This is only a quick fix to avoid triggering the autosaving on fetching the inspection
    setDocumentMarkups: (state, action: PayloadAction<DocumentMarkupsPayload>) => {
      state[action.payload.documentType].annotations = action.payload.annotations;
      state[action.payload.documentType].pageRange = action.payload.pageRange;
    },
    // This is only a quick fix to avoid triggering autosaving when we still don't have the search selected
    setZoneAnnotations: (state, action: PayloadAction<AllAnnotationsPayload>) => {
      state[action.payload.documentType].annotations = action.payload.annotations;
    },
    selectingZone: (state, action: PayloadAction<boolean>) => {
      state.selectingZone = action.payload;
    },
    startPolygonCreation: (
      state,
      action: PayloadAction<{
        documentType: DocumentTypes;
        pageNumber: number;
        currentPage: number;
      }>,
    ) => {
      state.activePolygonCreation = {
        isActive: true,
        documentType: action.payload.documentType,
        pageNumber: action.payload.pageNumber,
        currentPage: action.payload.currentPage,
      };
    },
    exitPolygonCreation: (state) => {
      state.activePolygonCreation = {
        isActive: false,
        documentType: null,
        pageNumber: null,
        currentPage: null,
      };
    },
    enterManualAlignMode: (
      state,
      action: PayloadAction<{
        cropZoneId: string;
        pageNumber: number;
        originZoneDocumentType: DocumentTypes;
      }>,
    ) => {
      state.manualAlignMode = {
        isActive: true,
        cropZoneId: action.payload.cropZoneId,
        cropZonePageNumber: action.payload.pageNumber,
        originZoneDocumentType: action.payload.originZoneDocumentType,
        masterAnchorPoint: null,
        targetAnchorPoint: null,
      };
    },
    setAnchorPoint: (
      state,
      action: PayloadAction<{
        type: 'master' | 'target';
        x: number;
        y: number;
        pageNumber: number;
      }>,
    ) => {
      if (state.manualAlignMode.isActive) {
        const anchorPoint = {
          x: action.payload.x,
          y: action.payload.y,
          pageNumber: action.payload.pageNumber,
        };
        if (action.payload.type === 'master') {
          state.manualAlignMode.masterAnchorPoint = anchorPoint;
        } else {
          state.manualAlignMode.targetAnchorPoint = anchorPoint;
        }
      }
    },
    exitManualAlignMode: (state) => {
      state.manualAlignMode = {
        isActive: false,
        cropZoneId: null,
        cropZonePageNumber: null,
        originZoneDocumentType: null,
        masterAnchorPoint: null,
        targetAnchorPoint: null,
      };
    },
    setOpenPanel: (state, action: PayloadAction<boolean>) => {
      state.openPanel = action.payload;
    },
    setDifferencePanelOpen: (state, action: PayloadAction<boolean>) => {
      state.differencePanelOpen = action.payload;
    },
    setChangeGridDirection: (state, action: PayloadAction<string>) => {
      state.changeGridDirection = action.payload;
    },
    setChangeGridWidth: (state, action: PayloadAction<number | null>) => {
      state.changeGridWidth = action.payload;
    },
    setPageRangeOption: (state, action: PayloadAction<PageRangeOption>) => {
      state.pageRangeOption = action.payload;
    },
    setShowLibrary: (state, action: PayloadAction<showLibraryPayload>) => {
      state.showLibrary[action.payload.type] = action.payload.value;
    },
    setDefaultInternalAnnotationsVisibility: (state, action: PayloadAction<boolean>) => {
      state.internalAnnotationsVisibility = action.payload;
      state.internalAnnotationsVisibilityDefault = action.payload;
    },
    setInternalAnnotationsVisibilityToDefault: (state) => {
      state.internalAnnotationsVisibility = state.internalAnnotationsVisibilityDefault;
    },
    setInternalAnnotationsVisibility: (state, action: PayloadAction<boolean>) => {
      state.internalAnnotationsVisibility = action.payload;
    },
    // Intent action: used by saga to start inspection after pending updates complete
    startInspectionAfterUpdatingAnnotations: (state) => state,
    setTextZoneId: (state, action: PayloadAction<number>) => {
      state.nextTextZoneId = action.payload;
    },
    setMarqueeZoneId: (state, action: PayloadAction<number>) => {
      state.nextMarqueeZoneId = action.payload;
    },
    setGraphicZoneId: (state, action: PayloadAction<number>) => {
      state.nextGraphicZoneId = action.payload;
    },
    setCropZoneId: (state, action: PayloadAction<number>) => {
      state.nextCropZoneId = action.payload;
    },
    setManualAlignZoneId: (state, action: PayloadAction<number>) => {
      state.nextManualAlignZoneId = action.payload;
    },
    setCrossoutZoneId: (state, action: PayloadAction<number>) => {
      state.nextCrossoutZoneId = action.payload;
    },
    setColorPointId: (state, action: PayloadAction<number>) => {
      state.nextColorPointId = action.payload;
    },
    increaseTextZoneId: (state) => {
      state.nextTextZoneId += 1;
    },
    decreaseTextZoneId: (state) => {
      state.nextTextZoneId -= 1;
    },
    increaseGraphicZoneId: (state) => {
      state.nextGraphicZoneId += 1;
    },
    increaseManualAlignZoneId: (state) => {
      state.nextManualAlignZoneId += 1;
    },
    increaseMarqueeZoneId: (state) => {
      state.nextMarqueeZoneId += 1;
    },
    increaseCrossoutZoneId: (state) => {
      state.nextCrossoutZoneId += 1;
    },
    decreaseCrossoutZoneId: (state) => {
      state.nextCrossoutZoneId -= 1;
    },
    increaseColorPointId: (state) => {
      state.nextColorPointId += 1;
    },
    setResyncCount: (state, action: PayloadAction<number>) => {
      state.settings.resyncCount = action.payload;
    },
    setCumulativeNetResyncedDifferences: (state, action: PayloadAction<number>) => {
      state.settings.cumulativeNetResyncedDifferences = action.payload;
    },
    setInspectionSettings: (state, action: PayloadAction<Partial<InspectionSettings>>) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    setAutoMatchGraphic: (state, action: PayloadAction<Partial<AutoMatchGraphic>>) => {
      state.autoMatchGraphic = { ...state.autoMatchGraphic, ...action.payload };
    },
    setSearchingForZone: (state, action: PayloadAction<boolean>) => {
      state.searchingForGraphicZone = action.payload;
    },
    setSelectedCustomDictionaryIDs: (state, action: PayloadAction<string[]>) => {
      state.customDictionaryIDs = action.payload;
    },
    setTableEnhancementEnabledState: (state, action: PayloadAction<boolean>) => {
      state.tableEnhancementEnabledState = action.payload;
    },
    setUpdatedAt: (state, action: PayloadAction<string>) => {
      state.updatedAt = action.payload;
    },
    setShiftedGraphicRefId: (state, action: PayloadAction<string>) => {
      state.shiftedGraphicRefId = action.payload;
    },
    setLayers: (state, action: PayloadAction<{ documentType: DocumentType; layers: Layer[] }>) => {
      state[action.payload.documentType].layers = action.payload.layers;
    },
    setSeparations: (state, action: PayloadAction<{ documentType: DocumentTypes; separations: Separation[] }>) => {
      state[action.payload.documentType].separations = action.payload.separations;
    },
    setOverprint: (state, action: PayloadAction<{ documentType: DocumentTypes; enabled: boolean }>) => {
      state[action.payload.documentType].overprint = action.payload.enabled;
    },
    setIsBarcode: (state, action: PayloadAction<boolean>) => {
      state.isBarcode = action.payload;
    },
    setUnmatchedPages: (state, action: PayloadAction<number[]>) => {
      state.unmatchedPages = action.payload;
    },
    setUnmatchedCropPages: (state, action: PayloadAction<number[]>) => {
      state.unmatchedCropPages = action.payload;
    },
    setSingleFileInspection: (state) => {
      state.settings.singleFile = true;
    },
    setIsResultsPanelHidden: (state, action: PayloadAction<boolean>) => {
      state.isResultsPanelHidden = action.payload;
    },
    setGraphicsOptions: (state, action: PayloadAction<{ key: GraphicsOptionKey; value: GraphicsOptionValue }>) => {
      state.settings.graphics = { ...state.settings.graphics, [action.payload.key]: action.payload.value };
    },
    setBarcodeSettings: (state, action: PayloadAction<Partial<BarcodeSettings>>) => {
      state.settings.barcodeSettings = { ...state.settings.barcodeSettings, ...action.payload };
    },
    toggleGraphics: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.settings.graphics = {
          [GraphicsOptionKey.Sensitivity]: GraphicsSensitivity.STANDARD,
          [GraphicsOptionKey.MatchingMode]: MatchingMode.OneToMany,
        };
        state.settings.fullPageGraphics = true;
      } else {
        delete state.settings.graphics;
        state.settings.fullPageGraphics = false;
      }
    },
    setFlashMode: (state, action: PayloadAction<boolean>) => {
      state.flashMode = action.payload;
    },
    setHighlightColor: (state, action: PayloadAction<ColorId>) => {
      state.settings.highlightColor = action.payload;
    },
    deleteOutdatedGraphicZones: (state) => {
      const isNotGraphicTool = (annotation: InputAnnotation) =>
        annotation.usedTool !== PDFTronTools.GRAPHIC &&
        annotation.usedTool !== PDFTronTools.SHIFTED_GRAPHIC &&
        annotation.usedTool !== PDFTronTools.SCALED_GRAPHIC;

      state.source.annotations = state.source.annotations.filter(isNotGraphicTool);
      state.target.annotations = state.target.annotations.filter(isNotGraphicTool);
    },
    setPolygons: (
      state,
      action: PayloadAction<{
        documentType: DocumentTypes;
        points: [number, number][][][] | '';
        type?: 'Layer' | 'Separation' | '';
        key?: string;
        layerOrSeparationName?: string;
        callSaveInspection?: boolean;
      }>,
    ) => {
      const { documentType, points, type, key, layerOrSeparationName } = action.payload;

      state.polygons = {
        ...state.polygons,
        [documentType]: {
          ...state.polygons[documentType],
          ...(points !== undefined && { points }),
          ...(key !== undefined && { key }),
          ...(type !== undefined && { type }),
          ...(layerOrSeparationName !== undefined && { layerOrSeparationName }),
        },
      };
    },
    setResyncToolToastHasShown: (state) => {
      state.settings.hasShownResyncToolToast = true;
    },
  },
});

export default {
  ...inspection,
  actions: {
    ...inspection.actions,
    selectZone,
    excludePage,
    excludePages,
  },
};
/* eslint-enable no-param-reassign */
